import { Component, OnInit } from '@angular/core';
import { UserService } from '../../shared/services/user.service';
import { UserSettings } from '../../shared/types/users/User';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef } from '@angular/material/dialog';
import { DateTime } from 'luxon';

@Component({
    selector: 'las-user-settings-modal',
    templateUrl: './user-settings-modal.component.html',
    styleUrls: ['./user-settings-modal.component.scss'],
    standalone: false,
})
export class UserSettingsModalComponent implements OnInit {
    isLoading = true;
    isSaving = false;
    settings!: UserSettings;
    settingsForm: FormGroup;
    notification: FormControl;
    time: FormGroup;
    hoursOptions: string[];
    minutesOptions: string[];

    /**
     * Constructs the modal form fields.
     * @param userService Service for getting and saving the user settings.
     * @param snack Snackbar (toast) service.
     * @param dialogRef Ref used to close the dialog.
     * @param fb The form builder.
     */
    constructor(
        private userService: UserService,
        private snack: MatSnackBar,
        private dialogRef: MatDialogRef<UserSettingsModalComponent>,
        fb: FormBuilder,
    ) {
        const zeros = '00';
        this.notification = new FormControl(Option.Immediately);
        this.time = fb.group({
            hours: ['12'],
            minutes: [zeros],
            meridiem: [Meridiem.Pm],
        });
        this.settingsForm = fb.group({
            notification: this.notification,
            time: this.time,
        });
        this.hoursOptions = this.getStringNumbers(12);
        this.minutesOptions = [zeros, ...this.getStringNumbers(59)];
    }

    /**
     * Gets the hours control from the time form group.
     */
    get hours() {
        return this.time.controls[Control.Hours.toLowerCase()];
    }

    /**
     * Gets the minutes control from the time form group.
     */
    get minutes() {
        return this.time.controls[Control.Minutes.toLowerCase()];
    }

    /**
     * Gets the meridiem control from the time form group.
     */
    get meridiem() {
        return this.time.controls[Control.Meridiem.toLowerCase()];
    }

    /**
     * Gets the UTC notify time from settings as saved in the API.
     */
    get utcNotifyDate() {
        return this.settings.notificationSettings.notifyTime;
    }

    /**
     * Gets the luxon DateTime in the local/system timezone from UTC. If the current notify time doesn't exist or is
     * invalid, the default local 'now' DateTime is returned.
     */
    get pstNotifyTime(): DateTime {
        let date = DateTime.fromJSDate(new Date(this.utcNotifyDate)).toLocal();
        if (!date?.isValid) {
            console.error(
                'Cannot convert null-ish time to locale. Setting to default local "now" DateTime',
                date,
            );
            date = DateTime.local();
        }
        return date;
    }

    /**
     * Loads the user's settings.
     */
    ngOnInit(): void {
        this.userService
            .getUserSettings()
            .subscribe((settings) => this.setSettings(settings));
    }

    /**
     * Saves the updates to the user's settings, closes the modal, and shows a success toast.
     */
    saveSettings() {
        this.isSaving = true;
        const settings = this.getSettingsFromForm();
        this.userService.saveSettings(settings).subscribe((_) => {
            this.dialogRef.close();
            this.snack.open('Settings saved successfully!', 'Close', {
                duration: 3000,
            });
        });
    }

    private setSettings(settings: UserSettings) {
        this.settings = settings;
        if (
            this.settings.notificationSettings.notifyMethod === Option.Summary
        ) {
            this.notification.setValue(Option.Summary);
            const pst = this.pstNotifyTime;
            this.setSettingsOnForm(pst);
        }
        this.isLoading = false;
    }

    private setSettingsOnForm(pst: DateTime) {
        let hours = pst.hour;
        // The hours are in 24-hour format; we must set the meridiem accordingly.
        const meridiem = hours >= 12 ? Meridiem.Pm : Meridiem.Am;
        this.meridiem.setValue(meridiem);
        // Normalize the hours to 12-hour and handle midnight (0-hour).
        hours = hours % 12;
        hours = hours ? hours : 12;
        this.hours.setValue(this.zeroPad(hours));
        this.minutes.setValue(this.zeroPad(pst.minute));
    }

    private getSettingsFromForm() {
        const settings: UserSettings = {
            notificationSettings: {
                notifyMethod: this.notification.value,
                notifyTime: this.settings.notificationSettings.notifyTime,
            },
            lastVersionSeen: this.settings.lastVersionSeen,
        };

        if (this.notification.value === Option.Summary) {
            // We must convert the hours because the time libs are 24-hour-based.
            const hours = this.convertHoursTo24(
                +this.hours.value,
                this.meridiem.value,
            );
            const minutes = +this.minutes.value;
            // Only time matters so setting a meaningless date here.
            const now = DateTime.local();
            const localTime = DateTime.local(now.year, now.month, now.day, hours, minutes, 0, 0);

            settings.notificationSettings.notifyTime = localTime.toUTC().toJSDate();
        } else
        {
            settings.notificationSettings.notifyTime = DateTime.utc().toJSDate();
        }
        return settings;
    }

    private convertHoursTo24(hours: number, meridiem: Meridiem) {
        // Handle PM hours 1-11.
        if (meridiem == Meridiem.Pm && hours < 12) return hours + 12;
        // Handle midnight.
        if (meridiem == Meridiem.Am && hours === 12) return 0;
        // Time was noon.
        return hours;
    }

    private zeroPad = (n: number) => (n < 10 ? `0${n}` : n.toString());

    private getStringNumbers = (number: number) =>
        [...Array(number).keys()].map((n: number) => this.zeroPad(++n));

    protected readonly Option = Option;
    protected readonly Meridiem = Meridiem;
    protected readonly Control = Control;
}

/**
 * Values for the notification method options.
 */
export enum Option {
    Immediately = 'Immediately',
    Summary = 'Summary',
}

/**
 * Values for the meridiem form field.
 */
export enum Meridiem {
    Am = 'AM',
    Pm = 'PM',
}

/**
 * Form control names.
 */
enum Control {
    Hours = 'hours',
    Minutes = 'minutes',
    Meridiem = 'meridiem',
}
